/**
 * useRevealOnScroll — one-shot IntersectionObserver hook.
 *
 * Returns `{ ref, visible }`. Attach `ref` to the container, then flip CSS
 * classes based on `visible`. Pair with the `.reveal-stagger` / `.reveal-item`
 * utility classes in src/index.css for a staggered fade-up reveal.
 *
 * Why a hook (not inline IO in each component):
 *   - We now use this pattern in 2+ places (home trust strip, collections
 *     grid). Sharing avoids drift in threshold/rootMargin tuning.
 *   - Graceful degrade: SSR or browsers without IntersectionObserver get
 *     `visible: true` immediately so the content never stays hidden.
 *
 * @param {Object} [options]
 * @param {number} [options.threshold=0]    How much of the element must be in
 *                                          view before triggering (0–1).
 *                                          Default is 0 (first pixel): ratio
 *                                          thresholds silently NEVER fire when
 *                                          the observed container is taller
 *                                          than the viewport (e.g. a 15-card
 *                                          single-column grid on mobile),
 *                                          leaving content stuck at opacity 0.
 * @param {string} [options.rootMargin]     Standard IO rootMargin string.
 */
import { useEffect, useRef, useState } from "react";

export default function useRevealOnScroll({
    threshold = 0,
    rootMargin = "0px 0px -40px 0px",
} = {}) {
    const ref = useRef(null);
    const [visible, setVisible] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined" || !("IntersectionObserver" in window)) {
            setVisible(true);
            return;
        }
        const el = ref.current;
        if (!el) return;
        const io = new IntersectionObserver(
            (entries) => {
                for (const e of entries) {
                    if (e.isIntersecting) {
                        setVisible(true);
                        io.disconnect();   // one-shot
                        break;
                    }
                }
            },
            { threshold, rootMargin }
        );
        io.observe(el);
        return () => io.disconnect();
    }, [threshold, rootMargin]);
    return { ref, visible };
}
